import { EmptyState, Badge, Button, Track } from '../../../components/ui';
import Box from './Box';
import { FaBookOpen } from '../../../components/ui/icons';
import { humanize } from '../../../lib/formatLabels';
import type { CommitmentsResponse, StudyPlan } from '../../../api/types';
import { commitmentPace } from '../lib/derive';
import { dayMonth } from '../lib/format';

type Commitment = CommitmentsResponse['active'][number];

interface Props {
  plan: StudyPlan | null;
  /** The student's active commitment: the artboard's study-plan summary. */
  commitment?: Commitment | null;
  /** Switches to the Study plan tab. */
  onOpenPlan?: () => void;
}

function formatWeekOf(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  } catch {
    return iso;
  }
}

/** The four milestones are the 25 / 50 / 75 / 100% marks the backend fires. */
const MILESTONES = 4;

function CommitmentSummary({ commitment, onOpenPlan }: { commitment: Commitment; onOpenPlan?: () => void }) {
  const pace = commitmentPace(commitment);
  const fired = (commitment.milestones_fired ?? []).length;
  const unit = (commitment as any).target_unit ? ` ${(commitment as any).target_unit}` : '';
  return (
    <div>
      <p className="font-semibold text-ink">{humanize(commitment.kind)}</p>
      <p className="mb-3 mt-1 text-[13px] text-muted">
        {dayMonth(commitment.started_at)} → {dayMonth(commitment.deadline)} · target {commitment.target_value}{unit}
      </p>
      <div className="flex items-center gap-2.5">
        <Track className="flex-1" value={(fired / MILESTONES) * 100} tone="accent" label="Milestones reached" />
        <span className="font-mono text-[13px] text-ink">{fired} / {MILESTONES} milestones</span>
      </div>
      {onOpenPlan && (
        <Button variant="ghost" className="mt-4 w-full justify-center" onClick={onOpenPlan}>Open study plan</Button>
      )}
      {pace && <span className="sr-only">{pace === 'behind' ? 'Behind the pace needed' : 'On track'}</span>}
    </div>
  );
}

export default function StudyPlanPanel({ plan, commitment = null, onOpenPlan }: Props) {
  if (commitment) {
    const pace = commitmentPace(commitment);
    return (
      <Box title="Study plan" help="study_plan"
        actions={pace ? <Badge tone={pace === 'behind' ? 'warning' : 'success'} size="sm">{pace === 'behind' ? 'Behind' : 'On track'}</Badge> : undefined}>
        <CommitmentSummary commitment={commitment} onOpenPlan={onOpenPlan} />
        {plan && plan.chapters.length > 0 && (
          <div className="mt-4 border-t border-hairline pt-3.5">
            <p className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">AI study plan · week of {formatWeekOf(plan.week_of)}</p>
            <ol className="space-y-2 text-[13px]">
              {plan.chapters.map((c, idx) => (
                <li key={c.chapter_id} className="flex gap-2.5">
                  <span className="font-mono text-xs font-semibold text-accent-ink">{idx + 1}</span>
                  <span className="min-w-0"><b className="font-semibold text-ink">{c.name}</b> <span className="text-muted">· {c.subject}</span></span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </Box>
    );
  }
  if (plan === null) {
    return (
      <Box title="AI study plan" help="study_plan">
        <EmptyState compact
          icon={<FaBookOpen />}
          title="No plan this week"
          body="Activity hasn't generated enough signal yet, or the nightly job hasn't run." />
      </Box>
    );
  }

  return (
    <Box title="AI study plan" help="study_plan" actions={<span className="font-mono text-xs text-muted">Week of {formatWeekOf(plan.week_of)}</span>}>
      <ol className="space-y-3">
        {plan.chapters.map((c, idx) => (
          <li key={c.chapter_id} className="grid grid-cols-[28px_minmax(0,1fr)] items-start gap-3 text-sm">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-bg font-mono text-xs font-semibold text-accent-ink">{idx + 1}</div>
            <div>
              <div className="font-semibold text-ink">
                {c.name}
                <span className="ml-2 text-xs text-muted">· {c.subject}</span>
              </div>
              <p className="mt-1 text-muted">{c.rationale}</p>
            </div>
          </li>
        ))}
      </ol>
    </Box>
  );
}
